import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { storesApi } from '@/services/api';
import { SeoMeta } from '@/components/seo/SeoMeta';
import { Store, MapPin, Package, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export const StoresPage = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await storesApi.getAll();
        setStores(response.data.stores || response.data);
      } catch (error) {
        console.error('Error fetching stores:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
  }, []);

  const filteredStores = stores.filter(store => 
    store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.county?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="stores-page">
      <SeoMeta
        title="Byggvaruhus och återförsäljare"
        description="Utforska anslutna byggvaruhus på ByggFynd. Hitta butiker i hela Sverige och deras produktsortiment."
        canonicalPath="/butiker"
      />
      {/* Header */}
      <div className="bg-[#003B73] py-12">
        <div className="container-main">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-4">
            Byggvaruhus
          </h1>
          <p className="text-gray-300 max-w-2xl">
            Upptäck alla våra anslutna byggvaruhus och deras produktsortiment.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="container-main py-6">
        <div className="bg-white rounded-md shadow-sm p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Sök efter butik, ort eller län..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <Link to="/karta">
              <Button variant="outline" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Visa på karta
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Store Grid */}
      <div className="container-main pb-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-md h-64 animate-pulse" />
            ))}
          </div>
        ) : filteredStores.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStores.map((store) => (
              <Link
                key={store.id}
                to={`/butik/${store.id}`}
                className="bg-white rounded-md shadow-sm overflow-hidden hover:shadow-md transition-shadow group"
                data-testid={`store-card-${store.id}`}
              >
                {/* Store Header */}
                <div className="h-32 bg-gradient-to-br from-[#003B73] to-[#004d99] relative">
                  {store.logo_url && (
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2">
                      <div className="w-20 h-20 bg-white rounded-lg shadow-md overflow-hidden border-4 border-white">
                        <img src={store.logo_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Store Info */}
                <div className="p-4 pt-12">
                  <h3 className="font-bold text-lg text-[#0F172A] group-hover:text-[#003B73] transition-colors">
                    {store.name}
                  </h3>
                  
                  <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                    <MapPin className="h-4 w-4" />
                    <span>{store.location}, {store.county}</span>
                  </div>

                  {store.description && (
                    <p className="text-sm text-gray-600 mt-3 line-clamp-2">
                      {store.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Package className="h-4 w-4" />
                      <span>{store.product_count || 0} produkter</span>
                    </div>
                    <span className="flex items-center text-sm font-semibold text-[#FF8A00] group-hover:underline">
                      Visa butik <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-md p-12 text-center">
            <Store className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Inga butiker hittades</h3>
            <p className="text-gray-500">Prova att söka med andra ord.</p>
          </div>
        )}
      </div>
    </div>
  );
};
